import { useDueLeadsQuery } from "@/api/leads.api";
import { useAuth } from "@/auth";
import { AppointmentReminderToast } from "@/components/AppointmentReminderToast";
import { useRouter } from "@tanstack/react-router";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";

const REMINDER_INTERVALS_MINUTES = [15, 1];

interface CallReminder {
  leadId: number;
  leadName: string;
  callTime: string;
  notifiedIntervals: Set<number>;
}

interface AppointmentRemindersContextType {
  reminders: Map<number, CallReminder>;
}

interface AppointmentRemindersProviderProps {
  children: ReactNode;
}

export function AppointmentRemindersProvider({
  children,
}: AppointmentRemindersProviderProps) {
  const router = useRouter();
  const { user } = useAuth();
  const remindersRef = useRef<AppointmentRemindersContextType["reminders"]>(
    new Map(),
  );

  const { data, isLoading } = useDueLeadsQuery({
    pageSize: 20,
    userIds: user ? [user.id] : undefined,
  });

  const upcomingLeads = useMemo(() => {
    if (!data) return [];
    const now = new Date().toISOString();
    return data.results
      .map((ld) => ld.lead)
      .filter((lead) => lead.followUpTime && lead.followUpTime >= now);
  }, [data]);

  const showReminderToast = useCallback(
    (reminder: CallReminder, minutesUntil: number, duration: number) => {
      toast.custom(
        (t) => (
          <AppointmentReminderToast
            leadName={reminder.leadName}
            callTime={reminder.callTime}
            minutesUntil={minutesUntil}
            onDismiss={() => toast.dismiss(t)}
            onViewLead={() => {
              toast.dismiss(t);
              router.navigate({
                to: "/player/$id",
                params: { id: reminder.leadId },
              });
            }}
          />
        ),
        {
          duration: duration,
        },
      );
      setTimeout(() => {
        toast.dismiss();
      }, duration);
    },
    [router],
  );

  const checkAndNotifyReminder = useCallback(
    (reminder: CallReminder, now: Date) => {
      const callTimeDate = new Date(reminder.callTime);
      const diffMs = callTimeDate.getTime() - now.getTime();
      const diffMinutes = diffMs / 60000;

      for (const interval of REMINDER_INTERVALS_MINUTES) {
        const isInRange =
          diffMinutes >= interval - 1 && diffMinutes <= interval;
        const alreadyNotified = reminder.notifiedIntervals.has(interval);

        if (isInRange && !alreadyNotified) {
          showReminderToast(reminder, diffMinutes, 60000);
          reminder.notifiedIntervals.add(interval);
          break;
        }
      }

      if (diffMinutes < -1) {
        remindersRef.current.delete(reminder.leadId);
      }
    },
    [showReminderToast],
  );

  const checkReminders = useCallback(() => {
    const now = new Date();
    remindersRef.current.forEach((reminder) =>
      checkAndNotifyReminder(reminder, now),
    );
  }, [checkAndNotifyReminder]);

  useEffect(() => {
    if (isLoading || upcomingLeads.length === 0) return;

    const leadIds = new Set(upcomingLeads.map((lead) => lead.id));

    upcomingLeads.forEach((lead) => {
      const existing = remindersRef.current.get(lead.id);
      remindersRef.current.set(lead.id, {
        leadId: lead.id,
        leadName: lead.name,
        callTime: lead.followUpTime!,
        notifiedIntervals: existing?.notifiedIntervals ?? new Set(),
      });
    });

    remindersRef.current.forEach((_, leadId) => {
      if (!leadIds.has(leadId)) {
        remindersRef.current.delete(leadId);
      }
    });
  }, [upcomingLeads, isLoading]);

  useEffect(() => {
    const interval = setInterval(checkReminders, 10000);
    return () => clearInterval(interval);
  }, [checkReminders]);

  return <>{children}</>;
}
